import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { DeckWithCards } from '@/hooks/useDecks';

export type DeckPreviewCard = {
  card_id: string;
  quantity: number;
  name: string;
  image_url: string | null;
  ink_color: string;
};

export type PublicDeck = DeckWithCards & {
  profile_username?: string;
  profile_emoji?: string;
  avg_rating?: number;
  rating_count?: number;
  user_rating?: number;
  preview_cards?: DeckPreviewCard[];
};

export function usePublicDecks() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['public-decks'],
    queryFn: async () => {
      const { data: decks, error } = await supabase
        .from('decks')
        .select('*')
        .eq('published', true)
        .order('updated_at', { ascending: false })
        .limit(30);
      if (error) throw error;
      if (!decks?.length) return [];

      const userIds = [...new Set(decks.map(d => d.user_id))];
      const deckIds = decks.map(d => d.id);

      const [{ data: profiles }, { data: deckCards }, { data: ratings }] = await Promise.all([
        supabase.from('profiles').select('user_id, username, avatar_emoji').in('user_id', userIds),
        supabase.from('deck_cards').select('deck_id, card_id, quantity').in('deck_id', deckIds),
        supabase.from('deck_ratings').select('deck_id, user_id, rating').in('deck_id', deckIds),
      ]);

      // Fetch card info for previews
      const allCardIds = [...new Set((deckCards || []).map(dc => dc.card_id))];
      const { data: cardInfos } = allCardIds.length
        ? await supabase.from('cards').select('id, name, image_url, ink_color').in('id', allCardIds)
        : { data: [] };
      const cardMap = new Map((cardInfos || []).map(c => [c.id, c]));

      const profileMap = new Map((profiles || []).map(p => [p.user_id, p]));
      const countMap = new Map<string, number>();
      const previewMap = new Map<string, DeckPreviewCard[]>();
      (deckCards || []).forEach(dc => {
        countMap.set(dc.deck_id, (countMap.get(dc.deck_id) || 0) + dc.quantity);
        const existing = previewMap.get(dc.deck_id) || [];
        const card = cardMap.get(dc.card_id);
        if (card && existing.length < 5) {
          existing.push({ card_id: dc.card_id, quantity: dc.quantity, name: card.name, image_url: card.image_url, ink_color: card.ink_color });
        }
        previewMap.set(dc.deck_id, existing);
      });

      const ratingMap = new Map<string, { sum: number; count: number; userRating?: number }>();
      (ratings || []).forEach(r => {
        const existing = ratingMap.get(r.deck_id) || { sum: 0, count: 0 };
        existing.sum += r.rating;
        existing.count += 1;
        if (user && r.user_id === user.id) existing.userRating = r.rating;
        ratingMap.set(r.deck_id, existing);
      });

      return decks.map(d => {
        const r = ratingMap.get(d.id);
        return {
          ...d,
          cards: [],
          total_cards: countMap.get(d.id) || 0,
          preview_cards: previewMap.get(d.id) || [],
          profile_username: profileMap.get(d.user_id)?.username || 'Unknown',
          profile_emoji: profileMap.get(d.user_id)?.avatar_emoji || '🧙',
          avg_rating: r ? r.sum / r.count : 0,
          rating_count: r?.count || 0,
          user_rating: r?.userRating,
        };
      }) as PublicDeck[];
    },
  });
}

export function useTogglePublish() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ deckId, published }: { deckId: string; published: boolean }) => {
      const { error } = await supabase.from('decks').update({ published } as any).eq('id', deckId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['decks'] });
      qc.invalidateQueries({ queryKey: ['public-decks'] });
    },
  });
}

export function useRateDeck() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ deckId, rating }: { deckId: string; rating: number }) => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase.from('deck_ratings').upsert(
        { deck_id: deckId, user_id: user.id, rating } as any,
        { onConflict: 'deck_id,user_id' }
      );
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['public-decks'] }),
  });
}

export function useCopyDeck() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sourceDeckId: string) => {
      if (!user) throw new Error('Not authenticated');
      // Get source deck
      const { data: source, error: srcErr } = await supabase
        .from('decks').select('*').eq('id', sourceDeckId).single();
      if (srcErr || !source) throw new Error('Deck not found');

      // Create copy
      const { data: newDeck, error: createErr } = await supabase
        .from('decks')
        .insert({
          user_id: user.id,
          name: `${source.name} (Copy)`,
          description: source.description,
          ink_1: source.ink_1,
          ink_2: source.ink_2,
        })
        .select().single();
      if (createErr || !newDeck) throw createErr || new Error('Failed to create deck');

      // Copy cards
      const { data: srcCards } = await supabase
        .from('deck_cards').select('card_id, quantity').eq('deck_id', sourceDeckId);
      if (srcCards?.length) {
        const inserts = srcCards.map(c => ({
          deck_id: newDeck.id,
          card_id: c.card_id,
          quantity: c.quantity,
        }));
        await supabase.from('deck_cards').insert(inserts);
      }
      return newDeck;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['decks'] }),
  });
}
